import React, { useContext, useEffect, useCallback } from "react";
import { FOCUS_BORDER } from "../../config/focusStyles";
import { FiltersContext } from "./filter.context";
import { useCategoryList } from "../../utils/hooks/useCategoryList";
import { useGenreList } from "../../utils/hooks/useGenreList";
import { contentTypeFilters, sortingFilters } from "../../config/enums";
import FocusableSelectBoundary from "../common/focusableSelectBoundary";
import { useCountryList } from "../../utils/hooks/useCountryList";
import { CloseRounded } from "../legacy-ui/icons";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { ButtonBase } from "../legacy-ui/materialCompat";
import { useInputMode } from "../../utils/hooks/useInputMode";

export function CategoryFilter({ setCategory, category }) {
  const [selected, setSelected] = React.useState();

  const { items, isLoading } = useCategoryList();

  useEffect(() => {
    setSelected(category);
  }, [category]);

  return (
    <FocusableSelectBoundary
      className="legacy-search-filter-select"
      instanceFocusKey={"category-filter"}
      label={"دسته بندی"}
      options={items}
      onChange={(val) => {
        setCategory(val);
        setSelected(val);
      }}
      value={selected}
      mode="single"
      isLoading={isLoading}
    />
  );
}

export function GenreFilter({ setGenre, genre }) {
  const [selected, setSelected] = React.useState();
  const { isLoading, items } = useGenreList();

  useEffect(() => {
    setSelected(genre);
  }, [genre]);

  return (
    <FocusableSelectBoundary
      className="legacy-search-filter-select"
      instanceFocusKey={"genre-filter"}
      label={"ژانر"}
      options={items}
      onChange={(val) => {
        setGenre(val);
        setSelected(val);
      }}
      isLoading={isLoading}
      value={selected}
      mode="single"
    />
  );
}

export function CountryFilter() {
  const { setCountry } = useContext(FiltersContext);
  const [selected, setSelected] = React.useState();
  const { items, isLoading } = useCountryList();

  return (
    <FocusableSelectBoundary
      className="legacy-search-filter-select"
      instanceFocusKey={"country-filter"}
      label={"کشور سازنده"}
      options={items}
      onChange={(val) => {
        setCountry(val);
        setSelected(val);
      }}
      value={selected}
      mode="single"
      isLoading={isLoading}
    />
  );
}

export const SortingTypeFilter = () => {
  const { setSortingType } = useContext(FiltersContext);
  const [selected, setSelected] = React.useState();

  return (
    <FocusableSelectBoundary
      className="legacy-search-filter-select"
      instanceFocusKey={"sortingType-filter"}
      label={"ترتیب نمایش"}
      options={sortingFilters}
      onChange={(val) => {
        setSortingType(val);
        setSelected(val);
      }}
      value={selected}
      mode="single"
    />
  );
};

export const ContentTypeFilter = ({ setKind, kind }) => {
  const [selected, setSelected] = React.useState();

  useEffect(() => {
    setSelected(kind);
  }, [kind]);

  return (
    <FocusableSelectBoundary
      className="legacy-search-filter-select"
      instanceFocusKey={"contentType-filter"}
      label={"نوع محتوا"}
      options={contentTypeFilters}
      onChange={(val) => {
        setKind(val);
        setSelected(val);
      }}
      mode="single"
      value={selected}
    />
  );
};

export const ResetFiltersButton = ({ resetFilters }) => {
  const { focusKey, ref, focused, focusSelf } = useFocusable({
    onEnterPress: resetFilters,
    focusKey: "reset-filter-btn",
  });
  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  return (
    <FocusContext.Provider value={focusKey}>
      <ButtonBase
        ref={ref}
        onClick={resetFilters}
        onMouseEnter={handleMouseEnter}
        size="large"
        isIconOnly
        style={{
          border: focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive,
          backgroundColor: "#333",
          borderRadius: "50%",
          padding: 10,
          margin: "0 1rem",
        }}
      >
        <CloseRounded fontSize={"large"} htmlColor="#bfbfbf" />
      </ButtonBase>
    </FocusContext.Provider>
  );
};

const SearchButton = ({
  onEnterPress = () => { },
  onFocus = () => { },
  loading = false,
}) => {
  const { ref, focused, focusSelf } = useFocusable({
    onEnterPress,
    onFocus,
  });
  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  return (
    <div
      ref={ref}
      className={"legacy-search-button-container" + (focused ? " is-focused" : "")}
      onClick={onEnterPress}
      onMouseEnter={handleMouseEnter}
    >
      <button className="legacy-search-button-base" disabled={loading}>
        <div className="legacy-search-button-inner">
          {loading ? (
            <div className="legacy-search-spinner" />
          ) : (
            <span className="legacy-search-button-text">جستجو</span>
          )}
        </div>
      </button>
      <div className="legacy-search-button-border" />
    </div>
  );
};

const SearchFilters = ({
  onSearch,
  resetFilters,
  mountReset,
  setFilters,
  filters,
}) => {
  const { ref, focusKey } = useFocusable({
    focusKey: "search-filters-parent",
  });

  const setCategory = (category) => {
    setFilters((prev) => ({ ...prev, category }));
  };

  const setGenre = (genre) => {
    setFilters((prev) => ({ ...prev, genre }));
  };

  const setKind = (kind) => {
    setFilters((prev) => ({ ...prev, kind }));
  };

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="legacy-search-filters-row" ref={ref}>
        <ContentTypeFilter setKind={setKind} kind={filters.kind} />
        <CategoryFilter setCategory={setCategory} category={filters.category} />
        <GenreFilter setGenre={setGenre} genre={filters.genre} />
        <div className="legacy-search-filter-actions">
          {mountReset && <ResetFiltersButton resetFilters={resetFilters} />}
          <SearchButton onEnterPress={onSearch} />
        </div>
      </div>
    </FocusContext.Provider>
  );
};

export default SearchFilters;
